import { useState } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { useComments } from '../hooks/useComments';
import { useGetEmployeesQuery } from '../api/commentsApi';
import { CommentEditor } from './CommentEditor';
import { CommentItem } from './CommentItem';

interface CommentListProps {
  entityId: number;
  entityTypeId: number;
  canAdd?: boolean;
}

export const CommentList = ({ entityId, entityTypeId, canAdd = true }: CommentListProps) => {
  const {
    comments,
    isLoading,
    isAdding,
    isEditing: isSavingEdit,
    addComment,
    editComment,
    deleteComment,
    isError,
  } = useComments(entityId, entityTypeId);
  const { data: employees = [] } = useGetEmployeesQuery();
  const [editingId, setEditingId] = useState<number | null>(null);

  const handleSendComment = async (content: string, tags: string[]) => {
    try {
      const numericTags = tags.map(Number).filter((n) => !isNaN(n));
      await addComment(content, numericTags);
    } catch (err) {
      console.error('Failed to post comment', err);
    }
  };

  const handleSaveEdit = async (id: number, content: string, tags: string[]) => {
    try {
      const numericTags = tags.map(Number).filter((n) => !isNaN(n));
      await editComment(id, content, numericTags);
      setEditingId(null);
    } catch (err) {
      console.error('Failed to save edit', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteComment(id);
    } catch (err) {
      console.error('Failed to delete comment', err);
    }
  };

  return (
    <Box sx={{ mt: 4, width: '100%' }}>
      {!canAdd && (
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Comments ({comments.length})
        </Typography>
      )}

      {canAdd && (
        <CommentEditor
          label={`Comments (${comments.length})`}
          onSend={handleSendComment}
          isSubmitting={isAdding}
          availableUsers={employees}
          uploadContextId={entityId}
        />
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" variant="body2">
          Failed to load comments.
        </Typography>
      ) : comments.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No comments yet. Be the first to start the discussion!
        </Typography>
      ) : (
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'transparent' }}>
          {comments.map((comment) => (
            <CommentItem
              key={comment.CommentId}
              comment={comment}
              isEditing={editingId === comment.CommentId}
              isSaving={isSavingEdit}
              availableUsers={employees}
              uploadContextId={entityId}
              onEdit={(id) => setEditingId(id)}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={handleSaveEdit}
              onDelete={handleDelete}
            />
          ))}
        </Paper>
      )}
    </Box>
  );
};
